import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZES = [25, 50, 100, 250];

/**
 * Pagination — page controls for all table views.
 *
 * Props:
 *   totalCount       {number}    — total filtered row count
 *   pageSize         {number}    — rows per page (default 50)
 *   currentPage      {number}    — 1-based current page
 *   onPageChange     {Function}  — called with new page number
 *   onPageSizeChange {Function}  — called with new page size
 *   resetPage        {Function}  — called when parent signals filter/sort change
 */
export function Pagination({
  totalCount = 0,
  pageSize = 50,
  currentPage = 1,
  onPageChange,
  onPageSizeChange,
  resetPage,
}) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const start = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end   = Math.min(currentPage * pageSize, totalCount);

  function goTo(page) {
    const p = Math.max(1, Math.min(totalPages, page));
    if (p !== currentPage) onPageChange?.(p);
  }

  function handlePageInputKeyDown(e) {
    if (e.key === 'Enter') {
      const val = parseInt(e.target.value, 10);
      if (!isNaN(val)) goTo(val);
    }
  }

  function handleSizeChange(e) {
    const size = parseInt(e.target.value, 10);
    onPageSizeChange?.(size);
    onPageChange?.(1);
    resetPage?.();
  }

  const btnBase = {
    width: 30, height: 30,
    border: '1px solid #e2e8f0', borderRadius: 6,
    background: '#fff',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    transition: 'opacity 0.12s, background 0.12s',
  };

  return (
    <div
      data-testid="pagination"
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 10,
        padding: '8px 0',
      }}
    >
      {/* Showing X–Y of Z results */}
      <span
        data-testid="pagination-summary"
        style={{ fontSize: 13, color: '#64748b', flexShrink: 0 }}
      >
        {totalCount === 0
          ? 'No results'
          : `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${totalCount.toLocaleString()} results`
        }
      </span>

      {/* Controls — pushed to right */}
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>

        {/* Page size selector */}
        <select
          data-testid="page-size-selector"
          value={pageSize}
          onChange={handleSizeChange}
          style={{
            height: 30, fontSize: 12,
            border: '1px solid #e2e8f0', borderRadius: 6,
            padding: '0 6px', cursor: 'pointer', color: '#475569',
            background: '#fff',
          }}
        >
          {PAGE_SIZES.map(s => (
            <option key={s} value={s}>{`${s} / page`}</option>
          ))}
        </select>

        {/* Previous page */}
        <button
          data-testid="prev-page-btn"
          onClick={() => goTo(currentPage - 1)}
          disabled={currentPage <= 1}
          style={{
            ...btnBase,
            cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
            opacity: currentPage <= 1 ? 0.4 : 1,
          }}
        >
          <ChevronLeft size={14} style={{ color: '#475569' }} />
        </button>

        {/* Page indicator + direct input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#475569' }}>
          <span>Page</span>
          <input
            data-testid="page-number-input"
            type="number"
            defaultValue={currentPage}
            key={currentPage}
            min={1}
            max={totalPages}
            onKeyDown={handlePageInputKeyDown}
            style={{
              width: 46, height: 28, textAlign: 'center',
              border: '1px solid #e2e8f0', borderRadius: 6,
              fontSize: 12, padding: '0 4px',
              color: '#1e293b',
            }}
          />
          <span style={{ whiteSpace: 'nowrap' }}>
            of {totalPages.toLocaleString()}
          </span>
        </div>

        {/* Next page */}
        <button
          data-testid="next-page-btn"
          onClick={() => goTo(currentPage + 1)}
          disabled={currentPage >= totalPages}
          style={{
            ...btnBase,
            cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
            opacity: currentPage >= totalPages ? 0.4 : 1,
          }}
        >
          <ChevronRight size={14} style={{ color: '#475569' }} />
        </button>
      </div>
    </div>
  );
}
